import { Box, Typography, IconButton, Button, Tooltip, useMediaQuery } from '@mui/material'
import { styled, keyframes } from '@mui/material/styles'
import { useAccount, useConnect, useDisconnect } from 'wagmi'
import {
  AccountBalanceWallet,
  Logout,
  ContentCopy,
  Check,
} from '@mui/icons-material'
import { useState, useCallback } from 'react'
import { COLORS } from '../../constants/colors'
import { TEXT } from '../../constants/text'

/* ── animations ──────────────────────────────────────────── */
const shimmer = keyframes`
  0%   { background-position:  200% 0; }
  100% { background-position: -200% 0; }
`

/* ── styled bits ─────────────────────────────────────────── */
const Pill = styled('button')(() => ({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 16px',
  borderRadius: 999,
  border: '2px solid transparent',
  color: '#fff',
  fontWeight: 600,
  fontSize: '0.85rem',
  fontFamily: 'inherit',
  cursor: 'pointer',
  background: 'linear-gradient(135deg, rgba(0,0,0,0.65), rgba(0,0,0,0.45))',
  boxShadow: `0 0 20px rgba(255,215,0,0.12)`,
  transition: 'transform .2s, box-shadow .2s',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    padding: 2,
    borderRadius: 999,
    background: `conic-gradient(from 0deg, #ff0099, ${COLORS.gold.primary}, ${COLORS.green.neon}, ${COLORS.blue.primary}, ${COLORS.purple.primary}, #ff0099)`,
    WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
  },
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: `0 0 28px rgba(255,215,0,0.22)`,
  },
}))

const Addr = styled(Typography)(() => ({
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '0.82rem',
  fontWeight: 600,
  letterSpacing: '0.4px',
  userSelect: 'none',
}))

/* ── component ───────────────────────────────────────────── */
export default function WalletButton() {
  const { address, isConnected } = useAccount()
  const { connect, connectors } = useConnect()
  const { disconnect } = useDisconnect()
  const [copied, setCopied] = useState(false)
  const mobile = useMediaQuery('(max-width:600px)')

  const handleConnect = useCallback(() => {
    const c = connectors[0]
    if (c) connect({ connector: c })
  }, [connect, connectors])

  const handleCopy = useCallback(() => {
    if (!address) return
    navigator.clipboard.writeText(address)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }, [address])

  const short = address
    ? `${address.slice(0, mobile ? 4 : 6)}...${address.slice(mobile ? -3 : -4)}`
    : ''

  if (!isConnected) {
    return (
      <Pill onClick={handleConnect}>
        <AccountBalanceWallet sx={{ fontSize: 18 }} />
        {TEXT.WALLET.CONNECT}
      </Pill>
    )
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Pill onClick={handleCopy}>
        <AccountBalanceWallet sx={{ fontSize: 16 }} />
        <Addr>{short}</Addr>
        {copied ? (
          <Check sx={{ fontSize: 14, color: COLORS.green.primary }} />
        ) : (
          <ContentCopy sx={{ fontSize: 14, opacity: 0.5 }} />
        )}
      </Pill>

      <Tooltip title={TEXT.WALLET.DISCONNECT}>
        <IconButton
          onClick={() => disconnect()}
          size="small"
          sx={{
            color: COLORS.text.muted,
            '&:hover': { color: COLORS.red.primary },
          }}
        >
          <Logout sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  )
}
